import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { cn, formatCurrency, formatDateTime, getStatusColor, getStatusLabel, getChannelInfo } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Search,
  Download,
  Package,
  Eye,
  FileText,
  Truck,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Printer,
  X,
  CheckSquare,
  Filter,
  ChevronDown,
} from "lucide-react";

import CargoLabelSidebar from "@/components/CargoLabelSidebar";
import EmptyState from "@/components/common/EmptyState";
import { TableRowSkeleton } from "@/components/common/Skeletons";

const hasRealInvoice = (order) => Boolean(
  order?.invoice_number ||
  order?.parasut_invoice_id ||
  order?.bizimhesap_invoice_guid
);

const getOrderChannel = (order) => String(order?.channel || "").trim().toLowerCase();

const isLocalCargoProvider = (provider) => ["hepsijet", "yurtici"].includes(String(provider || "").trim().toLowerCase());

const getOrderLocalBarcodes = (order) => {
  const existing = Array.isArray(order?.cargo_barcodes)
    ? order.cargo_barcodes.filter((item) => String(item?.barcode || "").trim())
    : [];
  if (existing.length > 0) return existing;

  const tracking = String(order?.cargo_tracking || "").trim();
  if (!tracking) return [];

  const channel = getOrderChannel(order);
  if (isLocalCargoProvider(order?.cargo_provider) || !["trendyol", "hepsiburada", "n11"].includes(channel)) {
    return [{ barcode: tracking, created_at: order?.cargo_created_at || "" }];
  }
  return [];
};

const getOrderCargoCodesForDisplay = (order) => {
  const localCodes = getOrderLocalBarcodes(order).map((item) => String(item?.barcode || "").trim()).filter(Boolean);
  if (localCodes.length > 0) return localCodes;

  const channel = getOrderChannel(order);
  if (channel === "n11") {
    const tracking = String(order?.n11_cargo_tracking || "").trim();
    return tracking ? [tracking] : [];
  }
  if (channel === "hepsiburada") {
    const packageNo = String(order?.hb_package_number || order?.cargo_tracking || "").trim();
    return packageNo ? [packageNo] : [];
  }
  if (channel === "trendyol") {
    const packageNo = String(order?.trendyol_package_id || order?.channel_order_id || "").trim();
    return packageNo ? [packageNo] : [];
  }
  return [];
};

const hasPrintableCargoLabel = (order) => {
  const channel = getOrderChannel(order);
  if (channel === "trendyol" || channel === "hepsiburada") return true;
  return getOrderCargoCodesForDisplay(order).length > 0;
};

const canCreateShipmentForOrder = (order) => {
  const status = String(order?.status || "").trim().toLowerCase();
  if (["cancelled", "deleted"].includes(status)) return false;
  const channel = getOrderChannel(order);
  if (channel === "trendyol" || channel === "hepsiburada") return false;
  if (channel === "n11") return !String(order?.n11_cargo_tracking || "").trim();
  return getOrderLocalBarcodes(order).length === 0;
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [invoicing, setInvoicing] = useState(null);
  const [shippingOrder, setShippingOrder] = useState(null);
  const [cargoSidebarOpen, setCargoSidebarOpen] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const perPage = 20;

  const fetchData = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }
      const [ordersRes, integsData] = await Promise.all([
        api.get(`/orders?${params.toString()}`),
        api.get("/integrations"),
      ]);
      setOrders(ordersRes.items || []);
      setTotalPages(ordersRes.total_pages || 1);
      setTotalOrders(ordersRes.total || 0);
      setIntegrations(integsData);
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(currentPage, searchQuery); }, [fetchData, currentPage, searchQuery]);

  const wcIntegration = integrations.find(i => i.provider === "woocommerce");

  const handleSyncOrders = async () => {
    if (!wcIntegration) {
      toast.error("WooCommerce entegrasyonu bulunamadi");
      return;
    }
    setSyncing(true);
    try {
      const result = await api.post(`/woocommerce/${wcIntegration.id}/sync-orders`);
      if (result.success) {
        toast.success(result.message);
        await fetchData(currentPage, searchQuery);
      }
    } catch (error) {
      toast.error(error.message || "Siparis senkronizasyonu basarisiz");
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateInvoice = async (orderId) => {
    setInvoicing(orderId);
    try {
      const result = await api.post(`/orders/${orderId}/create-invoice`);
      if (result.success) {
        toast.success(result.message);
        await fetchData(currentPage, searchQuery);
      } else {
        toast.error(result.message || "Fatura olusturulamadi");
      }
    } catch (error) {
      toast.error(error.message || "Fatura olusturma hatasi");
    } finally {
      setInvoicing(null);
    }
  };

  const handleCreateShipment = async (orderId) => {
    setShippingOrder(orderId);
    try {
      const result = await api.post(`/hepsijet/create-shipment/${orderId}`);
      if (result.success) {
        toast.success(result.message || "Kargo olusturuldu");
        await fetchData(currentPage, searchQuery);
        if (result.label_ready || result.barcode) {
          setSelectedOrderIds([orderId]);
          setCargoSidebarOpen(true);
        }
      } else {
        toast.error(result.message || "Kargo olusturulamadi");
      }
    } catch (error) {
      toast.error(error.message || "Kargo olusturma hatasi");
    } finally {
      setShippingOrder(null);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      (order.customer_name || "").toLowerCase().includes(q) ||
      (order.channel_order_id || "").toLowerCase().includes(q);
    const matchesStatus = !filterStatus || order.status === filterStatus;
    const matchesChannel = !filterChannel || (order.channel || "").toLowerCase() === filterChannel;
    const orderDate = order.created_at ? new Date(order.created_at) : null;
    const matchesFrom = !filterDateFrom || (orderDate && orderDate >= new Date(filterDateFrom));
    const matchesTo = !filterDateTo || (orderDate && orderDate <= new Date(filterDateTo + "T23:59:59"));
    return matchesSearch && matchesStatus && matchesChannel && matchesFrom && matchesTo;
  });

  const activeChannels = [...new Set(orders.map(o => o.channel).filter(Boolean))];
  const activeFilterCount = [filterStatus, filterChannel, filterDateFrom, filterDateTo].filter(Boolean).length;
  const handleSearchChange = (value) => { setSearchQuery(value); setCurrentPage(1); };

  const getCargoStatusBadge = (order) => {
    const hasBarcodes = hasPrintableCargoLabel(order);
    const cargoError = String(order?.cargo_error_message || "").trim();
    if (cargoError) {
      return (
        <Badge variant="destructive" className="text-xs rounded-full" title={cargoError}>
          <AlertCircle className="h-3 w-3" />
          Hata
        </Badge>
      );
    }
    if (hasBarcodes && order.cargo_status === "delivered") {
      return <Badge variant="success" className="text-xs rounded-full"><CheckCircle2 className="h-3 w-3" />Teslim</Badge>;
    }
    if (hasBarcodes) {
      const count = order.cargo_barcodes?.length || 1;
      return <Badge variant="success" className="text-xs rounded-full"><Truck className="h-3 w-3" />{count > 1 ? `${count} Barkod` : "Hazır"}</Badge>;
    }
    if (order.status === "cancelled") {
      return <Badge variant="muted" className="text-xs rounded-full">—</Badge>;
    }
    return <Badge variant="warning" className="text-xs rounded-full"><Clock className="h-3 w-3" />Bekliyor</Badge>;
  };

  const getInvoiceStatusBadge = (order) => {
    if (hasRealInvoice(order)) {
      return <Badge variant="success" className="text-xs rounded-full"><CheckCircle2 className="h-3 w-3" />Faturalandi</Badge>;
    }
    if (order.invoice_status === "pending" || !order.invoice_status) {
      return <Badge variant="warning" className="text-xs rounded-full"><Clock className="h-3 w-3" />Bekliyor</Badge>;
    }
    return <Badge variant="muted" className="text-xs rounded-full"><AlertCircle className="h-3 w-3" />{order.invoice_status}</Badge>;
  };

  return (
    <div className="page-shell space-y-6" data-testid="orders-page">
      <div className="page-header mb-2">
        <div className="app-page-header">
          <h1 className="app-page-title">Siparişler</h1>
          <p className="app-page-subtitle">{!loading && totalOrders > 0 ? `${totalOrders} sipariş` : "Tüm kanallardan gelen siparişlerinizi yönetin."}</p>
        </div>
        <div className="page-header-actions">
          <Button variant="outline" onClick={() => setCargoSidebarOpen(true)} data-testid="cargo-label-btn">
            <Printer className="mr-2 h-4 w-4" />Kargo Fisi
          </Button>
          <Button variant="outline" onClick={handleSyncOrders} disabled={syncing} data-testid="sync-orders-btn">
            {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            {syncing ? "Cekiliyor..." : "Siparisleri Cek"}
          </Button>
          <Button variant="outline" data-testid="export-orders-btn">
            <Download className="mr-2 h-4 w-4" />Disa Aktar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="surface-card-soft">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Siparis no veya musteri adi ara..." className="pl-9" value={searchQuery} onChange={(e) => handleSearchChange(e.target.value)} data-testid="search-orders-input" />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={cn("shrink-0 gap-1.5", showFilters && "bg-muted")}
                data-testid="filter-toggle-btn"
              >
                <Filter className="h-4 w-4" />
                Filtrele
                {activeFilterCount > 0 && (
                  <span className="ml-0.5 bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", showFilters && "rotate-180")} />
              </Button>
            </div>
            {showFilters && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 border-t">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Durum</label>
                  <select
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="filter-status"
                  >
                    <option value="">Tümü</option>
                    <option value="pending">Bekliyor</option>
                    <option value="processing">İşlemde</option>
                    <option value="shipped">Kargoda</option>
                    <option value="delivered">Teslim Edildi</option>
                    <option value="cancelled">İptal</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Kanal</label>
                  <select
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={filterChannel}
                    onChange={(e) => setFilterChannel(e.target.value)}
                    data-testid="filter-channel"
                  >
                    <option value="">Tümü</option>
                    {activeChannels.map((ch) => (
                      <option key={ch} value={ch.toLowerCase()}>{ch}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Başlangıç</label>
                  <input
                    type="date"
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    data-testid="filter-date-from"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Bitiş</label>
                  <input
                    type="date"
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    data-testid="filter-date-to"
                  />
                </div>
                {activeFilterCount > 0 && (
                  <div className="col-span-2 sm:col-span-4 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 text-muted-foreground"
                      onClick={() => { setFilterStatus(""); setFilterChannel(""); setFilterDateFrom(""); setFilterDateTo(""); }}
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Filtreleri Temizle
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Toolbar */}
      {selectedOrderIds.length > 1 && (
        <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2.5 animate-in">
          <CheckSquare className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium text-foreground">{selectedOrderIds.length} sipariş seçildi</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setCargoSidebarOpen(true); }}
              className="h-7 text-xs"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Toplu Etiket
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedOrderIds([])}
              className="h-7 text-xs text-muted-foreground"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Seçimi Kaldır
            </Button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="table-shell overflow-hidden">
        <div className="sheet-heading">
          <div>
            <h2>Siparişler</h2>
            {!loading && orders.length > 0 && <p>{orders.length} sipariş</p>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-5 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={filteredOrders.length > 0 && filteredOrders.every(o => selectedOrderIds.includes(o.id))}
                      onCheckedChange={(checked) => {
                        setSelectedOrderIds(checked ? filteredOrders.map(o => o.id) : []);
                      }}
                    />
                    Sipariş
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Müşteri</th>
                <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Tutar</th>
                <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Durum</th>
                <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Kargo</th>
                <th className="px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Fatura</th>
                <th className="px-5 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="p-0"><TableRowSkeleton cols={6} rows={8} /></td></tr>
              ) : filteredOrders.length === 0 ? (
                <tr><td colSpan={7} className="p-0">
                  {orders.length === 0 ? (
                    <EmptyState
                      icon={<Package strokeWidth={1.5} />}
                      title="Henüz sipariş yok"
                      desc="Entegrasyonunuzu bağlayın ve siparişlerinizi çekin. Tüm kanallarınızdaki siparişler burada görünecek."
                      action={{ label: "Entegrasyona Git", onClick: () => window.location.href = "/panel/entegrasyonlar" }}
                    />
                  ) : (
                    <EmptyState
                      icon={<Search strokeWidth={1.5} />}
                      title="Sipariş bulunamadı"
                      desc="Arama veya filtre kriterlerinizi değiştirmeyi deneyin."
                      size="sm"
                    />
                  )}
                </td></tr>
              ) : filteredOrders.map((order) => {
                const channelInfo = getChannelInfo(order.channel);
                const hasPrintableLabel = hasPrintableCargoLabel(order);
                const canCreateShipment = canCreateShipmentForOrder(order);
                return (
                  <tr
                    key={order.id}
                    className={cn("hover:bg-muted/30 transition-colors cursor-pointer", order.status === "cancelled" && "opacity-60", selectedOrderIds.includes(order.id) && "bg-primary/5")}
                    data-testid={`order-row-${order.id}`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedOrderIds.includes(order.id)}
                          onCheckedChange={(checked) => {
                            setSelectedOrderIds(prev =>
                              checked ? [...prev, order.id] : prev.filter(id => id !== order.id)
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center overflow-hidden shrink-0">
                          <img
                            src={`/integrations/${order.channel}.svg`}
                            alt={order.channel}
                            className="w-5 h-5 object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.parentElement.innerHTML = `<span class="text-xs font-semibold">${(channelInfo.label[0] || "?")}</span>`;
                            }}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">#{String(order.channel_order_id || "").replace(/^(WC-|HB-|TY-|TY|AZ-|N11-)/i, "")}</p>
                          <p className="text-xs text-muted-foreground">{formatDateTime(order.created_at)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium truncate max-w-[160px]">{order.customer_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold tabular-nums">{formatCurrency(order.total_amount)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1", getStatusColor(order.status))}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">{getCargoStatusBadge(order)}</td>
                    <td className="px-4 py-3">{getInvoiceStatusBadge(order)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {hasPrintableLabel ? (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" onClick={() => { setSelectedOrderIds([order.id]); setCargoSidebarOpen(true); }} title="Kargo Etiketi" data-testid={`print-label-${order.id}`}>
                            <Printer className="h-4 w-4" />
                          </Button>
                        ) : canCreateShipment ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => handleCreateShipment(order.id)}
                            data-testid={`create-shipment-${order.id}`}
                          >
                            <Truck className="h-3 w-3 mr-1" />
                            Kargo
                          </Button>
                        ) : null}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedOrder(order)} data-testid={`view-order-${order.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!hasRealInvoice(order) && order.status !== "cancelled" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => handleCreateInvoice(order.id)}
                            disabled={invoicing === order.id}
                            data-testid={`invoice-order-${order.id}`}
                          >
                            {invoicing === order.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
                            Fatura
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Siparis Detayi - {selectedOrder?.channel_order_id}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {(() => {
                const cargoCodes = getOrderCargoCodesForDisplay(selectedOrder);
                const hasPrintableLabel = hasPrintableCargoLabel(selectedOrder);
                const canCreateShipment = canCreateShipmentForOrder(selectedOrder);
                return (
                  <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Musteri</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                  <p className="text-sm">{selectedOrder.customer_email}</p>
                  <p className="text-sm">{selectedOrder.customer_phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Teslimat Adresi</p>
                  <p className="font-medium">{selectedOrder.shipping_address}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Urunler</p>
                <div className="border rounded-lg divide-y">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between p-3">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-muted-foreground">{item.quantity} adet x {formatCurrency(item.unit_price)}</p>
                      </div>
                      <p className="font-semibold">{formatCurrency(item.total_price)}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground">Siparis Durumu</p>
                  <span className={cn("inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 mt-1", getStatusColor(selectedOrder.status))}>
                    {getStatusLabel(selectedOrder.status)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Kargo</p>
                  {cargoCodes.length > 0 ? (
                    <div className="space-y-1 mt-1">
                      {cargoCodes.map((code, i) => (
                        <p key={i} className="font-mono text-xs font-medium">{code}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium">-</p>
                  )}
                  {hasPrintableLabel && (
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => { setSelectedOrderIds([selectedOrder.id]); setCargoSidebarOpen(true); setSelectedOrder(null); }}>
                      <Printer className="h-3 w-3 mr-1" /> Etiket Yazdir
                    </Button>
                  )}
                  {selectedOrder.cargo_error_message ? (
                    <p className="mt-2 text-xs text-rose-700">{selectedOrder.cargo_error_message}</p>
                  ) : null}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fatura</p>
                  <p className="font-medium">{selectedOrder.invoice_number || "Olusturulmadi"}</p>
                </div>
              </div>
              <div className="flex justify-between items-center pt-4 border-t">
                <span className="text-lg font-semibold">Toplam</span>
                <span className="text-2xl font-bold">{formatCurrency(selectedOrder.total_amount)}</span>
              </div>
              <div className="flex gap-2">
                {canCreateShipment && (
                  <Button className="flex-1" onClick={() => { handleCreateShipment(selectedOrder.id); setSelectedOrder(null); }} data-testid="detail-cargo-btn">
                    <Truck className="h-4 w-4 mr-2" />Kargo Olustur
                  </Button>
                )}
                {!hasRealInvoice(selectedOrder) && selectedOrder.status !== "cancelled" && (
                  <Button variant="outline" className="flex-1" onClick={() => { handleCreateInvoice(selectedOrder.id); setSelectedOrder(null); }} data-testid="detail-invoice-btn">
                    <FileText className="h-4 w-4 mr-2" />Faturandir
                  </Button>
                )}
              </div>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-muted-foreground">
            Toplam {totalOrders} sipariş, Sayfa {currentPage} / {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
            >
              Önceki
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                if (pageNum > totalPages) return null;
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}

      {/* Cargo Label Sidebar */}
      {(() => {
        const sidebarOrder = selectedOrderIds.length > 0
          ? filteredOrders.find((order) => order.id === selectedOrderIds[0]) || null
          : null;
        const sidebarProvider = String(sidebarOrder?.cargo_provider || sidebarOrder?.channel || "hepsijet").toLowerCase();
        return (
      <CargoLabelSidebar
        open={cargoSidebarOpen}
        onClose={() => { setCargoSidebarOpen(false); setSelectedOrderIds([]); }}
        selectedOrders={selectedOrderIds}
        orders={filteredOrders}
        onRefresh={fetchData}
        provider={sidebarProvider}
      />
        );
      })()}
    </div>
  );
}
